import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { InstantPick } from '../components/InstantPick'; // [FLUID-8 2026-10-12]
import styled, { useTheme } from 'styled-components';
import {
  fetchStats, fetchStatsMonths, StatsResponse, StatsRow, peekCache,
  fetchPayouts, refreshPayouts, PayoutSnapshot,
} from '../services/api';
import { useRealtime } from '../hooks/useRealtime'; // [REALTIME] 2026-06-23
import { Column, SortConfig } from '../types';
import DataTable from '../components/DataTable';
import { useAuth } from '../contexts/AuthContext'; // [COL-RESIZE-ALL 2026-09-29] keys the remembered column widths
import { TableSkeleton } from '../components/Skeleton';
import { LoadFailed, EmptyState } from '../components/LoadState'; // [LOAD-STATES 2026-09-29]
import { fmtDayTimeText, fmtShortDayWithYear } from '../utils/dates'; // [DATE-STYLE 2026-09-29]
import GlassSelect from '../components/GlassSelect';
import { pillTrack, pillSegment } from '../styles/buttons'; // [BUTTON-STYLE 2026-09-30]
import { lgButtonPrimary, LG_PRESS, LgPressProps } from '../styles/liquidGlass'; // [LIQUID-GLASS 2026-11-02]
import { SlideViewport, SlidePane, slidePos } from '../components/ViewSlide'; // [STATS-SLIDE 2026-10-08]


/* [PERF 2026-08-14] Locale formatters built ONCE — `toLocale*(locale, {options})`
 * constructs a fresh Intl formatter per call (measured 34us money / 90us date on V8,
 * vs 0.7us / 1.5us cached). Calls WITHOUT options are already fast and left alone. */
const MONEY_FMT = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const MONTH_LONG_YEAR_FMT = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' });

/* ── Styled components (mirrors RefundsPage's page chrome) ── */

const Container = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100%;
  min-width: 0;
  background: ${(p) => p.theme.surface};
  /* [DOCK-CLEARANCE 2026-07-06] Clear the floating tab dock at the end of the scroll
     so the last row never hides behind it. Must live on THIS (the page's own root) —
     Chromium doesn't scroll the scroll-container's (PageCard's) own padding-bottom into
     view. Same 88px per-root fix the other in-card tabs use. */
  padding-bottom: 88px;

  /* [MOBILE 2026-07-07] On phones the PageCard already supplies 88px of dock
     clearance for this (non-$flush) tab, so the root's own 88px on top of it —
     176px total — pushed the otherwise-fitting Stats table into a small vertical
     scroll (top clipped at the bottom of the scroll and vice-versa). Drop the
     redundant root clearance on mobile so short content fits with NO vertical
     scroll; the PageCard's 88px still keeps the last row clear of the dock, and
     if the account list ever does exceed the screen it simply scrolls normally. */
  @media (max-width: 768px) {
    /* [PHONE-LAYOUT 2026-10-23] Was padding-bottom 0. The page scrolls on a phone anyway, and the card's own 88px is NOT
       scrolled into view (the note at the top): at 360px the Stats view's last rows ended under the dock. The 88px end
       clearance is back. */
    padding-bottom: 88px;
  }
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  background: ${(p) => (p.theme.colorScheme === 'dark'
    ? 'linear-gradient(180deg, rgba(255,255,255,0.07) 0%, rgba(255,255,255,0.04) 100%)'
    : 'linear-gradient(180deg, rgba(241,245,249,0.85) 0%, rgba(229,232,231,0.72) 100%)')};
  border-bottom: 1px solid ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(22,25,23,0.10)')};
  box-shadow: inset 0 1px 0 ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.55)')};
  flex-wrap: wrap;
  gap: 10px;

  /* [MOBILE 2026-07-07] Stats has just a title + one month picker, so keep them
     on a single row (title left, filter right) on phones instead of stacking —
     matches the requested layout and reads cleaner than two stacked full-width
     rows. flex-wrap still catches any extreme narrow width.
     Also FREEZE this heading at the top on phones: position:sticky pins it to the
     top of the scrolling card so the title + month filter stay put while only the
     rows below scroll (no more "heading scrolls away at the bottom"). Opaque
     surface background so scrolling rows never show through it. */
  @media (max-width: 768px) {
    padding: 10px 12px;
    /* [PHONE-LAYOUT 2026-10-23, user] NOT frozen any more (was position: sticky, top 0): on a phone nothing is fixed at
       the top — the title, the switch and the month scroll away with the page ("in the phone view nothing will be fixed
       on the top side otherwise how will i see the data for the below"). */
    position: static;
    background: ${(p) => p.theme.surface};

    /* [STATS PHONE HEADER 2026-09-23, user] "stats will show in the middle and below that the
       pill". Supersedes the one-row phone layout above: title centred, then the Filters group
       centred under it (the pill first, then the Stats-view month picker; see Filters/Segmented). */
    flex-direction: column;
    align-items: center;
    gap: 8px;
  }
`;

const PageTitle = styled.h1`
  font-size: 16px;
  font-weight: 700;
  color: ${(p) => p.theme.text};
  margin: 0;

  /* [PHONE-LAYOUT 2026-10-23] phones: the shared phone page title (Lead Management's), 17 px heavy */
  @media (max-width: 768px) { font-size: 17px; font-weight: 800; }
`;

const Filters = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;

  /* [STATS PHONE HEADER 2026-09-23, user] Phones: one centred column, pill on top (Segmented
     order -1) and the month picker under it, so the pill sits in the SAME spot on both views
     instead of jumping when the Stats-only picker appears or disappears. */
  @media (max-width: 768px) {
    flex-direction: column;
    flex-wrap: nowrap;
    align-items: center;
  }
`;

/* [PAYOUT-DETAILS 2026-08-09] Stats / Payout Details segmented toggle — one pill split into two
   halves with a green thumb sliding under the active one. Deliberately the SAME component shape
   as the Dialing Report's PM/Sales switch (DialingReportPage.tsx) so the two toggles in the app
   read as one control, rather than inventing a second style for the same job. */
/* [BUTTON-STYLE 2026-09-30] Track and segment sizes come from styles/buttons (pillTrack / pillSegment). */
const Segmented = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: 1fr 1fr;
  ${pillTrack}
  /* [LIQUID-GLASS 2026-11-02, user] the track is pressed into the glass (as Edit Mapping's switch); the thumb is glossy */
  background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(0,0,0,0.30)' : 'rgba(255,255,255,0.66)')};
  border: 1px solid transparent;
  box-shadow: ${(p) => (p.theme.colorScheme === 'dark'
    ? 'inset 0 1px 2px rgba(0,0,0,0.5), inset 0 -1px 0 rgba(255,255,255,0.07)'
    : 'inset 0 1px 2px rgba(22,25,23,0.14), inset 0 0 0 1px rgba(22,25,23,0.07)')};

  /* [STATS PHONE HEADER 2026-09-23, user] Phones: the pill goes above the month picker. */
  @media (max-width: 768px) {
    order: -1;
  }
`;

const SegThumb = styled.div<{ $right: boolean }>`
  position: absolute;
  top: 3px;
  bottom: 3px;
  left: 3px;
  width: calc((100% - 6px) / 2);
  border-radius: 999px;
  background: linear-gradient(180deg, rgba(255,255,255,0.32), rgba(255,255,255,0) 55%),
    linear-gradient(180deg, ${(p) => p.theme.green600}, ${(p) => p.theme.green700});
  box-shadow: 0 2px 8px ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(11,102,64,0.32)' : 'rgba(20, 107, 84, 0.32)')},
    inset 0 1px 0 rgba(255,255,255,0.45);
  transform: translateX(${(p) => (p.$right ? '100%' : '0')});
  transition: transform 0.26s cubic-bezier(0.32, 0.72, 0, 1);
`;

const Seg = styled.button<{ $active: boolean }>`
  position: relative;
  z-index: 1;
  ${pillSegment}
  border: none;
  background: transparent;
  cursor: pointer;
  color: ${(p) => (p.$active ? '#fff' : p.theme.muted)};
  transition: color 180ms cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    color: ${(p) => (p.$active ? '#fff' : p.theme.colorScheme === 'dark' ? '#fff' : p.theme.text)};
  }

  /* The two labels differ a lot in length ("Stats" vs "Payout Details"), so on phones
     trim the padding rather than let the pill push the month picker off the row. */
  @media (max-width: 768px) {
    padding: 7px 12px;
    font-size: 12px;
  }
`;

const ScrollContainer = styled.div`
  overflow-x: auto;
  /* [FLUID-5 2026-10-12, user] "no need for scroller in the stats page": a sideways scroller is a vertical one too
     unless told otherwise, and a row rising into place for a moment showed a vertical scrollbar here. The page scrolls
     the table up and down; this box only ever scrolls sideways. */
  overflow-y: hidden;
`;

/* [STATS-SLIDE 2026-10-08, user] Stats <-> Payout Details SLIDES like the app's other view toggles, via the shared
   components/ViewSlide: going right (Stats -> Payout Details) the Stats table leaves to the LEFT while Payout Details
   comes in from the RIGHT; going back reverses it. Only the table part moves — the toolbar (title, month picker, pill)
   stays put, the user's rule for these toggles ("only the TABLE part shows the animation"). Both halves stay MOUNTED;
   both data loads already run at page open, so that changes nothing about loading. */
const STATS_VIEWS = ['stats', 'payout'] as const;

/* [PAYOUT-DETAILS 2026-08-09] The strip under the payout table: when it was last pulled, and the
   button that pulls it again. Below the table, as requested — the sheet put its Refresh button
   there too, so the reading order stays the one people already know. */
/* [STATS-TOOLBAR 2026-08-14] SETTLED LAYOUT — right-aligned single row with the Refresh Data
   button at the FAR RIGHT and Last Updated to its LEFT. Reached after four arrangements were
   tried and rejected on screen: the original left-aligned row, right-aligned with the button
   leading, a centred column with the timestamp stacked underneath, and left-aligned again.
   Do not "tidy" this back to one of those. The child order in the JSX is what puts the button
   last — see the note there.

   Right-aligned as ONE group (`flex-end`), never `space-between`: the error message shares this
   row and only appears sometimes, so a split would fling the button and the timestamp to
   opposite edges whenever there is no error.

   `flex-wrap` matters — button, timestamp and a possible error share the row, so a narrow
   viewport must wrap rather than squeeze the button. */
const PayoutFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 14px;
  flex-wrap: wrap;
  padding: 16px 20px 4px;

  @media (max-width: 768px) {
    padding: 14px 12px 4px;
  }
`;

const LastUpdated = styled.span`
  font-size: 12.5px;
  font-style: italic;
  color: ${(p) => p.theme.muted};
  font-variant-numeric: tabular-nums;
`;

// [BUTTON-STYLE 2026-09-30] The green primary from styles/buttons.
// [LIQUID-GLASS 2026-11-02, user] now the glossy green glass capsule (rim, shine, squish + press light), as Add Payment.
const RefreshButton = styled.button.attrs<LgPressProps>(LG_PRESS)`
  ${lgButtonPrimary}
`;

/* The refresh glyph spins only while a pull is running, so the button itself reports progress
   without needing a second spinner somewhere else on the page. */
const RefreshIcon = styled.span<{ $spinning: boolean }>`
  display: inline-block;
  font-size: 14px;
  line-height: 1;
  animation: ${(p) => (p.$spinning ? 'payout-spin 0.9s linear infinite' : 'none')};

  @keyframes payout-spin {
    to { transform: rotate(360deg); }
  }
`;

const PayoutError = styled.span`
  font-size: 12.5px;
  color: ${(p) => (p.theme.colorScheme === 'dark' ? '#f87171' : '#dc2626')};
`;

/* Status cell — the sheet's green "✓ Success" / red "❌ Failed", themed rather than hard-coded so
   it stays readable on both light and dark. */
const StatusText = styled.span<{ $tone: 'ok' | 'bad' | 'warn' }>`
  font-weight: 600;
  color: ${(p) => {
    const dark = p.theme.colorScheme === 'dark';
    if (p.$tone === 'ok') return dark ? '#4fdc95' : '#1e7c63';
    if (p.$tone === 'warn') return dark ? '#fbbf24' : '#b45309';
    return dark ? '#f87171' : '#dc2626';
  }};
`;


// Numeric cell — tabular figures, red for negatives (theme-aware), bold on the Total row.
const Num = styled.span<{ $neg?: boolean; $bold?: boolean }>`
  font-variant-numeric: tabular-nums;
  font-weight: ${(p) => (p.$bold ? 700 : 400)};
  color: ${(p) => (p.$neg ? (p.theme.colorScheme === 'dark' ? '#f87171' : '#dc2626') : 'inherit')};
`;

const AccountName = styled.span<{ $bold?: boolean }>`
  font-weight: ${(p) => (p.$bold ? 700 : 500)};
`;

/* ── Helpers ── */

type StatsTableRow = StatsRow & { _isTotal?: boolean };

function formatMonthFull(m: string): string {
  const [y, mo] = m.split('-');
  const date = new Date(Number(y), Number(mo) - 1);
  return MONTH_LONG_YEAR_FMT.format(date);
}

function fmtMoney(v: number): string {
  const abs = MONEY_FMT.format(Math.abs(v));
  return v < 0 ? `-$${abs}` : `$${abs}`;
}

// Subtle green tint marking the Total row — translucent so it reads on both themes
// (DataTable applies the returned colour directly as the row background).
const TOTAL_ROW_BG = 'rgba(20,107,84,0.10)';

// [PAYOUT-DETAILS 2026-08-09] Inert sort props for the payout table — see the DataTable use below.
const NO_SORT: SortConfig = { sortBy: '', sortOrder: 'asc' };
const noop = () => {};

/* ── Component ── */

// [PAYOUT-DETAILS 2026-08-09] Which half of the tab is showing. Defaults to 'payout' per the
// requested behaviour — the account matrix is still one click away on the left of the toggle.
type StatsView = 'stats' | 'payout';

const StatsPage: React.FC = () => {
  const { user } = useAuth(); // [COL-RESIZE-ALL 2026-09-29]
  const [view, setView] = useState<StatsView>('payout');
  const [months, setMonths] = useState<string[]>([]);
  const [month, setMonth] = useState('');
  const [data, setData] = useState<StatsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState<SortConfig>({ sortBy: '', sortOrder: 'asc' });

  // [PAYOUT-DETAILS 2026-08-09] Payout panel state. The load is a small state machine rather than
  // a boolean plus an "already loaded" ref: that pair could WEDGE. If the first request never settled
  // (dev hot-reload, a server restart mid-flight, a dropped connection) the ref said "already
  // loaded" while the flag said "still loading", so the fetch could never re-fire and the panel
  // showed skeletons for ever with no way back.
  const [payout, setPayout] = useState<PayoutSnapshot | null>(null);
  const [payoutLoad, setPayoutLoad] = useState<'idle' | 'loading' | 'done' | 'error'>('idle');
  const [payoutRefreshing, setPayoutRefreshing] = useState(false);
  const [payoutError, setPayoutError] = useState<string | null>(null);
  // [LOAD-STATES 2026-09-29, user] The month's stats failed to load: countdown card, not "No stats found".
  const [statsFailed, setStatsFailed] = useState(false);

  // Only accept a payload that is actually a snapshot. Rejecting it here as well as in the memo
  // means one odd response can never become the stored state and break every later render.
  const isSnapshot = (s: any): s is PayoutSnapshot => !!s && Array.isArray(s.rows);

  // Whether a load has been STARTED. A ref, not state, and `view` is the only dependency of the
  // effect below — deliberately. Putting the load state in the dep array is what broke this the
  // first time: the effect's own setPayoutLoad('loading') invalidated its deps, React ran the
  // cleanup, `active` flipped to false, and the GET's result was thrown away when it landed. The
  // panel then sat on skeletons for ever while Refresh Data (which does not go through this
  // effect) worked fine.
  const payoutStartedRef = useRef(false);

  // A remount means any request from the previous instance is gone and will never settle, so let
  // a fresh attempt through. Without this a hot-reload or a wedged first load could only be
  // recovered with a hard browser refresh.
  useEffect(() => { payoutStartedRef.current = false; }, []);

  // Read the stored snapshot the first time the panel is shown. Deliberately NOT on every switch
  // back: this is a manual-refresh panel, so re-reading on each toggle would add latency without
  // adding freshness. A failure lands in 'error' rather than back in 'idle', so a broken endpoint
  // cannot spin in a retry loop — Refresh Data is the way back.
  useEffect(() => {
    if (view !== 'payout' || payoutStartedRef.current) return;
    payoutStartedRef.current = true;
    let active = true;
    let settled = false;
    setPayoutLoad('loading');
    fetchPayouts()
      .then((res) => {
        settled = true;
        if (!active) return;
        if (isSnapshot(res)) setPayout(res);
        setPayoutLoad('done');
      })
      .catch((err) => {
        settled = true;
        console.error('Failed to load payouts:', err);
        if (!active) return;
        setPayoutError('Could not load payout details. Press Refresh Data to try again.');
        setPayoutLoad('error');
      });
    // Left mid-flight (e.g. switched to the Stats half before it landed): allow the next visit to
    // try again, rather than leaving the panel permanently "started" with nothing to show.
    return () => { active = false; if (!settled) payoutStartedRef.current = false; };
  }, [view]);

  // [LOAD-STATES 2026-09-29, user] The countdown card's retry for a failed FIRST read of the stored
  // snapshot (a GET — not Refresh Data, which pulls from Stripe and stays a manual button).
  const retryPayouts = useCallback(() => fetchPayouts()
    .then((res) => { if (isSnapshot(res)) setPayout(res); setPayoutError(null); setPayoutLoad('done'); })
    .catch((err) => { console.error('Failed to load payouts:', err); setPayoutLoad('error'); }), []);
  const payoutFailed = payoutLoad === 'error' && !payout;

  // Refresh Data — the button under the table. Skeletons replace the rows for the duration (the
  // pull is 2 Stripe calls per account, so it is genuinely slow) and the old figures are cleared
  // rather than left on screen, so nothing stale is ever read as current.
  const onRefreshPayouts = useCallback(async () => {
    setPayoutRefreshing(true);
    setPayoutError(null);
    try {
      const res = await refreshPayouts();
      if (isSnapshot(res)) setPayout(res);
      else setPayoutError('Refresh returned an unexpected response. The figures below are unchanged.');
      setPayoutLoad('done'); // also the way out of a failed first load
    } catch (err) {
      console.error('Failed to refresh payouts:', err);
      setPayoutError('Refresh failed. The stored figures below are unchanged.');
    } finally {
      setPayoutRefreshing(false);
    }
  }, []);

  // Load the month list once; default to the latest month with data. A light poll
  // (skipped while the tab is hidden) advances to a newer month when it appears,
  // without clobbering a month the user has deliberately navigated to.
  const latestSeenRef = useRef<string | null>(null);
  useEffect(() => {
    let active = true;
    const apply = (list: string[]) => {
      if (!active) return;
      setMonths(list);
      const target = list[0]; // already sorted desc by the server
      if (target && (latestSeenRef.current === null || target > latestSeenRef.current)) {
        latestSeenRef.current = target;
        setMonth((cur) => cur || target);
      }
    };
    const poll = () => fetchStatsMonths().then(apply).catch(() => {});
    poll();
    const id = setInterval(() => { if (document.hidden) return; poll(); }, 60000);
    return () => { active = false; clearInterval(id); };
  }, []);

  const load = useCallback(async (silent = false) => {
    if (!month) return;
    // [TAB-FAST 2026-07-07] Seed instantly from the SWR GET cache (idle-prefetch/prior-visit
    // warmed) so a warmed month paints at once with NO skeleton flash; a cold month still
    // shows it. Mirrors fetchStats(month) exactly so the key lines up. Miss = safe no-op.
    const seed = silent ? undefined : peekCache<StatsResponse>('/stats', { month });
    if (!silent) {
      if (seed) { setData(seed); setLoading(false); }
      else setLoading(true);
    }
    try {
      const res = await fetchStats(month);
      setData(res);
      setStatsFailed(false); // [LOAD-STATES 2026-09-29]
    } catch (err) {
      console.error('Failed to load stats:', err);
      // [LOAD-STATES 2026-09-29, user] A quiet refresh keeps the table; a real failed load shows the
      // countdown card (a cached copy of this month, if one is showing, stays instead).
      if (!silent) { setStatsFailed(true); if (!seed) setData(null); }
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => { load(); }, [load]);

  // [REALTIME] silently refresh when any underlying dataset changes.
  useRealtime(['payments', 'refunds', 'disputes'], () => load(true));

  const handleSort = (col: string) => {
    setSort((prev) => ({
      sortBy: col,
      sortOrder: prev.sortBy === col && prev.sortOrder === 'asc' ? 'desc' : 'asc',
    }));
  };

  // Sort only the account rows (client-side, in place); the Total row is always
  // pinned to the bottom regardless of the sort.
  const tableData: StatsTableRow[] = useMemo(() => {
    // [CACHE-REVALIDATE-PARSE 2026-09-23, user] Defensive on shape, the same guard the payout table
    // below has had since 2026-08-10. A payload that is not { rows, total } (it arrived as raw JSON
    // text from a background cache refresh, see api.ts revalidate) crashed the whole tab with "rows
    // is not iterable". Anything unrecognised is now "nothing to show" instead.
    if (!data || !Array.isArray(data.rows)) return [];
    let rows: StatsRow[] = data.rows;
    if (sort.sortBy) {
      const dir = sort.sortOrder === 'asc' ? 1 : -1;
      const k = sort.sortBy as keyof StatsRow;
      rows = [...rows].sort((a, b) => {
        const av = a[k];
        const bv = b[k];
        if (typeof av === 'number' || typeof bv === 'number') {
          return ((Number(av) || 0) - (Number(bv) || 0)) * dir;
        }
        return String(av ?? '').localeCompare(String(bv ?? '')) * dir;
      });
    }
    return [...rows, { ...data.total, _isTotal: true }];
  }, [data, sort]);

  const columns: Column[] = useMemo(() => {
    const money = (key: keyof StatsRow): Column['render'] => (v: any, row: any) => {
      const num = Number(v) || 0;
      return <Num $neg={num < 0} $bold={!!row._isTotal}>{fmtMoney(num)}</Num>;
    };
    return [
      { key: 'account_name', label: 'Account', sortable: true, width: '170px',
        render: (v: any, row: any) => <AccountName $bold={!!row._isTotal}>{v}</AccountName> },
      { key: 'payment', label: 'Payment', sortable: true, align: 'right', width: '120px', render: money('payment') },
      { key: 'refund', label: 'Refund', sortable: true, align: 'right', width: '110px', render: money('refund') },
      { key: 'dispute', label: 'Dispute', sortable: true, align: 'right', width: '110px', render: money('dispute') },
      { key: 'dispute_won', label: 'Dispute Won', sortable: true, align: 'right', width: '120px', render: money('dispute_won') },
      { key: 'in_bank', label: 'In Bank', sortable: true, align: 'right', width: '120px', render: money('in_bank') },
      { key: 'true_refund', label: 'True Refund', sortable: true, align: 'right', width: '120px', render: money('true_refund') },
      { key: 'false_refund', label: 'False Refund', sortable: true, align: 'right', width: '120px', render: money('false_refund') },
      { key: 'balance_after_true', label: 'Balance After True', sortable: true, align: 'right', width: '140px', render: money('balance_after_true') },
      { key: 'balance_after_false', label: 'Balance After False', sortable: true, align: 'right', width: '140px', render: money('balance_after_false') },
    ];
  }, []);

  // [GRAPHITE-EMERALD 2026-09-28] total-row tint: jade in light, emerald in dark.
  const scTheme = useTheme();
  const totalRowBg = scTheme.colorScheme === 'dark' ? 'rgba(11,102,64,0.10)' : TOTAL_ROW_BG;
  const rowBgFn = useCallback((row: any) => (row._isTotal ? totalRowBg : undefined), [totalRowBg]);

  // [PAYOUT-DETAILS 2026-08-09] Payout table — the sheet's columns A–F, plus its TOTAL row pinned
  // to the bottom. Same DataTable as the Stats matrix, so theming, the sticky header, hover and
  // the mobile behaviour are inherited rather than re-implemented.
  const payoutTableData = useMemo(() => {
    // Defensive on shape, not just on null. A response that arrives while the server is
    // restarting (or any proxy/error body that is not the snapshot) would otherwise be spread
    // here and crash the whole tab with "rows is not iterable" — a blank page for a transient
    // network event. Anything unrecognised is treated as "nothing to show" instead.
    const rows = Array.isArray(payout?.rows) ? (payout as PayoutSnapshot).rows : [];
    if (rows.length === 0) return [];
    const t = payout?.total;
    return [
      ...rows,
      {
        account_name: 'TOTAL',
        next_payout_date: '',
        available_balance: Number(t?.available_balance) || 0,
        pending_balance: Number(t?.pending_balance) || 0,
        total_payout_amount: Number(t?.total_payout_amount) || 0,
        status: '',
        _isTotal: true,
      },
    ];
  }, [payout]);

  const payoutUpdated = payout?.last_updated || null; // [DATE-STYLE 2026-09-29]
  const payoutColumns: Column[] = useMemo(() => {
    // [DATE-STYLE 2026-09-29] When the figures were pulled — the year for the payout day below.
    const payoutRefDate = payoutUpdated ? new Date(payoutUpdated.replace(' ', 'T')) : new Date();
    const money = (): Column['render'] => (v: any, row: any) => {
      const num = Number(v) || 0;
      return <Num $neg={num < 0} $bold={!!row._isTotal}>{fmtMoney(num)}</Num>;
    };
    return [
      { key: 'account_name', label: 'Account Name', width: '190px',
        render: (v: any, row: any) => <AccountName $bold={!!row._isTotal}>{v}</AccountName> },
      // [DATE-STYLE 2026-09-29, user] The server sends "Oct 02" (no year) → "Oct 2, 2026"; the year comes
      // from when the figures were pulled (a payout is at most a week after that).
      { key: 'next_payout_date', label: 'Next Payout Date', align: 'right', width: '140px',
        render: (v: any) => <Num>{v ? fmtShortDayWithYear(String(v), payoutRefDate) : '—'}</Num> },
      { key: 'available_balance', label: 'Available Balance', align: 'right', width: '150px', render: money() },
      { key: 'pending_balance', label: 'Pending Balance', align: 'right', width: '150px', render: money() },
      { key: 'total_payout_amount', label: 'Total Payout Amount', align: 'right', width: '170px', render: money() },
      { key: 'status', label: 'Status', width: '130px',
        render: (v: any, row: any) => {
          if (row._isTotal || !v) return null;
          if (v === 'success') return <StatusText $tone="ok">✓ Success</StatusText>;
          // A key that was never set is a configuration gap, not a Stripe failure — say which,
          // so a missing env var is not chased as an outage.
          if (v === 'missing_key') return <StatusText $tone="warn">No API key</StatusText>;
          return <StatusText $tone="bad">✕ Failed</StatusText>;
        } },
    ];
  }, [payoutUpdated]);

  return (
    <Container>
      <Toolbar>
        <PageTitle>Stats</PageTitle>
        <Filters>
          {/* [STATS-TOOLBAR 2026-08-14] Month picker LEFT, view pill RIGHT (was the other way
              round), and the picker renders ONLY on the Stats half.
              Payout Details is month-independent by construction — fetchPayouts() takes no month
              and the panel shows live Stripe balances — so a month control sitting over it was
              inert and implied a filter that did nothing.
              `month` is deliberately still STATE, not unmounted data: switching to Payout Details
              and back returns to the month you had chosen rather than snapping to the default. */}
          {view === 'stats' && (
            <GlassSelect liquid /* [LIQUID-GLASS 2026-11-02] glass capsule + glass list */ deferChange /* [FLUID-8 2026-10-12] instant pick */ value={month} onChange={(e) => setMonth(e.target.value)}>
              {months.length === 0 && <option value="">—</option>}
              {months.map((m) => (
                <option key={m} value={m}>{formatMonthFull(m)}</option>
              ))}
            </GlassSelect>
          )}
          {/* [FLUID-8 2026-10-12, user] InstantPick: the pill moves on the mouse PRESS; the page's switch follows. */}
          <InstantPick value={view} onPick={setView}>
            {(shown, press) => (
              <Segmented role="group" aria-label="Stats view">
                <SegThumb $right={shown === 'payout'} aria-hidden="true" />
                <Seg type="button" $active={shown === 'stats'} aria-pressed={shown === 'stats'} {...press('stats')}>
                  Stats
                </Seg>
                <Seg type="button" $active={shown === 'payout'} aria-pressed={shown === 'payout'} {...press('payout')}>
                  Payout Details
                </Seg>
              </Segmented>
            )}
          </InstantPick>
        </Filters>
      </Toolbar>

      {/* [PAYOUT-DETAILS 2026-08-09] Payout panel: the sheet's A–F table, then Last Updated and the
          Refresh Data button beneath it. Skeletons stand in for the rows on first load and for the
          whole of a refresh, so the table is never showing yesterday's figures while today's are
          still in flight. */}
      {/* [STATS-SLIDE 2026-10-08, user] Both halves stay mounted and slide: Payout Details rests on the
          RIGHT (its side of the toggle), Stats on the LEFT. */}
      <SlideViewport>
      <SlidePane $pos={slidePos(view, 'payout', STATS_VIEWS)} $quiet /* [FLUID-6 2026-10-12] a switch only slides */ aria-hidden={view !== 'payout'} data-stats-pane="payout">
          <ScrollContainer>
            {/* Skeletons stand in ONLY while a pull is actually running: on a Refresh Data press,
                and on the very first load when there is nothing stored to show yet. Once figures
                exist they STAY on screen — switching back to this panel must never blank a table
                that is already correct just to re-read the same stored snapshot. */}
            {payoutRefreshing || (payoutLoad === 'loading' && !payout) ? (
              <TableSkeleton columns={payoutColumns} />
            ) : payoutFailed ? (
              <LoadFailed what="payout details" onRetry={retryPayouts} />
            ) : payoutTableData.length === 0 ? (
              <EmptyState title="No payout details yet" hint="Press Refresh Data to pull them from Stripe." />
            ) : (
              <DataTable
                columns={payoutColumns}
                data={payoutTableData}
                /* Not sortable: the roster is a fixed 14 in the sheet's order, and the TOTAL row
                   is only meaningful at the bottom. DataTable requires the props, so they are
                   supplied inert rather than wiring a control nobody asked for. */
                sort={NO_SORT}
                onSort={noop}
                rowBgFn={rowBgFn}
                /* Six money columns read as a matrix, exactly like the Stats table — one card per
                   account on phones would lose the comparison the panel exists for. */
                disableMobileCards
                // [COL-RESIZE-ALL 2026-09-29, user] Drag a header's edge to resize (same as Payments).
                resizeStorageKey={`dt-stats-payouts-colw:${user?.id ?? 'anon'}`}
              />
            )}
          </ScrollContainer>

          {/* [STATS-TOOLBAR 2026-08-14] Order is deliberate: message, then timestamp, then the
              button LAST so it lands at the far right with the text to its left. The row is
              right-aligned (see PayoutFooter), so DOM order is also the visual order — swapping
              these back would put the button on the wrong side. */}
          <PayoutFooter>
            {payoutError && !payoutFailed && <PayoutError>{payoutError}</PayoutError> /* [LOAD-STATES 2026-09-29] the card says it */}
            {payout?.last_updated && !payoutRefreshing && (
              <LastUpdated>Last Updated: {fmtDayTimeText(payout.last_updated) /* [DATE-STYLE 2026-09-29] */} IST{/* [PAYOUT-IST 2026-10-16] the server sends India time now */}</LastUpdated>
            )}
            <RefreshButton type="button" onClick={onRefreshPayouts} disabled={payoutRefreshing}>
              <RefreshIcon $spinning={payoutRefreshing} aria-hidden="true">⟳</RefreshIcon>
              {payoutRefreshing ? 'Refreshing…' : 'Refresh Data'}
            </RefreshButton>
          </PayoutFooter>
      </SlidePane>
      <SlidePane $pos={slidePos(view, 'stats', STATS_VIEWS)} $quiet /* [FLUID-6 2026-10-12] a switch only slides */ aria-hidden={view !== 'stats'} data-stats-pane="stats">
      <ScrollContainer>
        {loading ? (
          <TableSkeleton columns={columns} />
        ) : statsFailed && !data ? (
          <LoadFailed what="stats" onRetry={() => load()} />
        ) : !data || !Array.isArray(data.rows) || data.rows.length === 0 ? (
          /* [CACHE-REVALIDATE-PARSE 2026-09-23, user] Array check: a non-{ rows } payload threw
             "Cannot read properties of undefined (reading 'length')" here once the memo was guarded. */
          <EmptyState title={`No stats for ${month ? formatMonthFull(month) : 'this month'}`} hint="Pick another month to see its figures." />
        ) : (
          <DataTable
            columns={columns}
            data={tableData}
            sort={sort}
            onSort={handleSort}
            rowBgFn={rowBgFn}
            /* [MOBILE 2026-07-07] Stats is a numeric reconciliation matrix — keep
               the real table (scrolls horizontally) on phones, like the website,
               instead of one meaningless card per account. */
            disableMobileCards
            // [COL-RESIZE-ALL 2026-09-29, user] Drag a header's edge to resize (same as Payments).
            resizeStorageKey={`dt-stats-colw:${user?.id ?? 'anon'}`}
          />
        )}
      </ScrollContainer>
      </SlidePane>
      </SlideViewport>
    </Container>
  );
};

export default StatsPage;
